import { PATHS } from "../routes/paths"

export const FACILITY_CLASSIFICATIONS = [
  { id: "airport", label: "Airport" },
  { id: "land-border", label: "Land Border" },
  { id: "seaport", label: "Seaport" },
  { id: "inland-port", label: "Inland Port" },
] as const

export type FacilityClassification = (typeof FACILITY_CLASSIFICATIONS)[number]["id"]

const ALL_CLASSIFICATIONS: FacilityClassification[] = FACILITY_CLASSIFICATIONS.map((c) => c.id)
const AIRPORT_ONLY: FacilityClassification[] = ["airport"]
const LAND_BORDER_ONLY: FacilityClassification[] = ["land-border"]
const SEAPORT_ONLY: FacilityClassification[] = ["seaport"]
const LAND_AND_AIRPORT: FacilityClassification[] = ["land-border", "airport"]
const LAND_AND_INLAND: FacilityClassification[] = ["land-border", "inland-port"]
const LAND_SEA_AIR: FacilityClassification[] = ["land-border", "airport", "seaport"]
const AIRPORT_SEAPORT: FacilityClassification[] = ["airport", "seaport"]
const AIRPORT_SEAPORT_INLAND: FacilityClassification[] = ["airport", "seaport", "inland-port"]
const LAND_AIR_INLAND: FacilityClassification[] = ["land-border", "airport", "inland-port"]
const AIRPORT_INLAND: FacilityClassification[] = ["airport", "inland-port"]
const LAND_SEA_INLAND: FacilityClassification[] = ["land-border", "seaport", "inland-port"]
const LAND_SEA_AIR_INLAND: FacilityClassification[] = ALL_CLASSIFICATIONS

export type ModuleDefinition = {
  id: string
  label: string
  category: string
  routes: (keyof typeof PATHS)[]
  defaultClassifications: FacilityClassification[]
}

const moduleDefinitions = [
  {
    id: "dashboard-overview",
    label: "Home",
    category: "Core Platform",
    routes: ["ROOT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "platform-settings",
    label: "Platform Settings",
    category: "Core Platform",
    routes: ["SETTINGS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "help-support",
    label: "Help & Support",
    category: "Core Platform",
    routes: ["HELP"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  { 
    id: "communications-call-lobby",
    label: "Call Lobby",
    category: "Communications",
    routes: ["CALL_LOBBY"],
  defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "communications-messaging",
    label: "Officer Messaging",
    category: "Core Platform",
    routes: ["COMMUNICATIONS_MESSAGES", "COMMUNICATIONS_THREAD"],
 
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-directory",
    label: "Traveller Directory",
    category: "Traveller",
    routes: ["TRAVELLERS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-pre-registrations",
    label: "Traveller Pre-Registrations",
    category: "Traveller",
    routes: ["TRAVELLERS_PRE", "TRAVELLERS_PRE_NEW"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-qr",
    label: "Traveller QR Scanner",
    category: "Traveller",
    routes: ["QR"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-thdf",
    label: "Traveller Health Declarations",
    category: "Traveller",
    routes: ["THDF_CASES", "THDF_CASES_CREATE", "THDF_CASES_LIST"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-screening",
    label: "Traveller Screening & Triage",
    category: "Traveller",
    routes: [
      "TRAVELLERS_SCREENING_LIST",
      "TRAVELLERS_SCREENING_CREATE",
      "TRAVELLERS_SCREENING_VIEW",
      "TRAVELLER_YF_LIST",
      "TRAVELLER_YF_CREATE",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-primary-screening",
    label: "Primary Screening",
    category: "Traveller",
    routes: [
      "TRAVELLER_PRIMARY_SCREENING_LIST",
      "TRAVELLER_PRIMARY_SCREENING_CREATE",
      "TRAVELLER_PRIMARY_SCREENING_VIEW",
      "TRAVELLER_PRIMARY_SCREENING_EDIT",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-surveillance",
    label: "Surveillance Cases",
    category: "Traveller",
    routes: ["SURVEILLANCE_CASES_LIST", "SURVEILLANCE_CASES_CREATE", "SURVEILLANCE_CASES_VIEW"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "traveller-ill-passenger",
    label: "Ill Traveller Reports",
    category: "Traveller",
    routes: ["PASSENGER_ILL_REPORTS_LIST", "PASSENGER_ILL_REPORTS_CREATE", "PASSENGER_ILL_REPORTS_VIEW"],
    defaultClassifications: AIRPORT_SEAPORT_INLAND,
  },
  {
    id: "conveyance-overview",
    label: "Conveyance Overview",
    category: "Conveyance",
    routes: ["CONVEYANCES"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "conveyance-general",
    label: "General Declarations",
    category: "Conveyance",
    routes: ["GENERAL_DECLARATIONS", "GENERAL_DECLARATIONS_CREATE"],
    defaultClassifications: AIRPORT_SEAPORT_INLAND,
  },
  {
    id: "conveyance-vehicle-general",
    label: "Vehicle General Declarations",
    category: "Conveyance",
    routes: [
      "VEHICLE_GENERAL_DECLARATIONS_LIST",
      "VEHICLE_GENERAL_DECLARATIONS_CREATE",
    ],
    defaultClassifications: LAND_AND_INLAND,
  },
  {
    id: "conveyance-vehicle-hazard",
    label: "Hazardous Cargo Checklists",
    category: "Conveyance",
    routes: [
      "VEHICLE_HAZARD_CHECKLISTS_LIST",
      "VEHICLE_HAZARD_CHECKLISTS_CREATE",
      "VEHICLE_HAZARD_CHECKLISTS_VIEW",
      "VEHICLE_HAZARD_CHECKLISTS_EDIT",
    ],
    defaultClassifications: LAND_AND_INLAND,
  },
  {
    id: "conveyance-passenger-train-inspections",
    label: "Passenger Train Inspections",
    category: "Conveyance",
    routes: [
      "PASSENGER_TRAIN_INSPECTIONS_LIST",
      "PASSENGER_TRAIN_INSPECTIONS_CREATE",
      "PASSENGER_TRAIN_INSPECTIONS_VIEW",
      "PASSENGER_TRAIN_INSPECTIONS_EDIT",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "conveyance-aircraft",
    label: "Aircraft Oversight",
    category: "Conveyance",
    routes: [
      "CONVEYANCES_AIRCRAFT",
      "CONVEYANCES_AIRCRAFT_ADD",
      "AIRCRAFT_CHECKLIST_VIEW",
      "AIRCRAFT_CHECKLIST_EDIT",
      "CONVEYANCE_CLEARANCE_LIST",
      "CONVEYANCE_CLEARANCE_CREATE",
      "INSPECTIONS_AIRCRAFT",
    ],
    defaultClassifications: AIRPORT_ONLY,
  },
  {
    id: "conveyance-road",
    label: "Road Inspections",
    category: "Conveyance",
    routes: [
      "CONVEYANCE_ROAD_INSPECTIONS_LIST",
      "CONVEYANCE_ROAD_INSPECTIONS_CREATE",
      "CONVEYANCE_BUS_CHECKLISTS_LIST",
      "CONVEYANCE_BUS_CHECKLISTS_CREATE",
      "CONVEYANCE_BUS_CHECKLISTS_VIEW",
      "CONVEYANCE_BUS_CHECKLISTS_EDIT",
    ],
    defaultClassifications: LAND_AIR_INLAND,
  },
  {
    id: "conveyance-checklists",
    label: "Conveyance Checklists",
    category: "Conveyance",
    routes: ["CONVEYANCE_CHECKLISTS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "conveyance-sanitation",
    label: "Sanitation Certificates",
    category: "Conveyance",
    routes: ["SANITATION_CERTS", "SANITATION_CERTS_CREATE"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "conveyance-holding-bay",
    label: "Holding & Search Bays",
    category: "Conveyance",
    routes: ["CONVEYANCE_HOLDING_BAY_LOGS", "CONVEYANCE_HOLDING_BAY_LOGS_CREATE"],
    defaultClassifications: LAND_AIR_INLAND,
  },
  {
    id: "conveyance-cases",
    label: "Conveyance Case Management",
    category: "Conveyance",
    routes: [
      "CASES_OPEN_LIST",
      "CASES_OPEN_CREATE",
      "CASES_PENDING",
      "CASES_PENDING_CREATE",
      "CASES_ESCALATED_LIST",
      "CASES_ESCALATED_CREATE",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "conveyance-vessels",
    label: "Vessel Oversight",
    category: "Conveyance",
    routes: ["CONVEYANCES_VESSELS"],
    defaultClassifications: SEAPORT_ONLY,
  },
  {
    id: "cargo-overview",
    label: "Cargo Overview",
    category: "Cargo",
    routes: ["INSPECTIONS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "cargo-inspections",
    label: "Cargo Inspections",
    category: "Cargo",
    routes: ["INSPECTIONS_CARGO", "INSPECTIONS_CARGO_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "cargo-hazardous",
    label: "Hazardous Substances",
    category: "Cargo",
    routes: ["CARGO_HAZARDOUS_SUBSTANCES_LIST", "CARGO_HAZARDOUS_SUBSTANCES_CREATE"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "cargo-certificates",
    label: "Inspection Certificates",
    category: "Cargo",
    routes: ["CARGO_CERTIFICATES_LIST", "CARGO_CERTIFICATES_CREATE"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "cargo-warehouse-inspections",
    label: "Warehouse Inspections",
    category: "Cargo",
    routes: ["CARGO_WAREHOUSE_INSPECTIONS_LIST", "CARGO_WAREHOUSE_INSPECTIONS_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "cargo-smuggled-goods-report",
    label: "Smuggled Goods Report",
    category: "Cargo",
    routes: ["SMUGGLED_GOODS_LIST"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "cargo-sampling",
    label: "Sampling & Lab Submissions",
    category: "Cargo",
    routes: ["CARGO_INSPECTION_OUTCOMES_LIST", "CARGO_INSPECTION_OUTCOMES_CREATE"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "cargo-holding",
    label: "Cargo Holding Bay",
    category: "Cargo",
    routes: ["CARGO_HOLDING_BAY_INSPECTIONS_LIST", "CARGO_HOLDING_BAY_INSPECTIONS_CREATE"],
    defaultClassifications: LAND_AIR_INLAND,
  },
  {
    id: "cargo-tire-inspections",
    label: "Tyre Inspections",
    category: "Cargo",
    routes: [
      "CARGO_TIRE_INSPECTIONS_LIST",
      "CARGO_TIRE_INSPECTIONS_CREATE",
      "CARGO_TIRE_INSPECTIONS_VIEW",
      "CARGO_TIRE_INSPECTIONS_EDIT",
    ],
    defaultClassifications: LAND_SEA_INLAND,
  },
  {
    id: "environmental-premises",
    label: "Premises Inspections",
    category: "Environmental Health",
    routes: ["INSPECTIONS_PREMISES", "INSPECTIONS_PREMISES_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "environmental-food-water",
    label: "Food & Water Safety",
    category: "Environmental Health",
    routes: ["INSPECTIONS_FOOD_WATER", "INSPECTIONS_FOOD_WATER_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "environmental-water-quality",
    label: "Water Quality Monitoring",
    category: "Environmental Health",
    routes: ["PREMISES_WATER_QUALITY_LIST", "PREMISES_WATER_QUALITY_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "environmental-sanitary",
    label: "Sanitary Audits",
    category: "Environmental Health",
    routes: ["INSPECTIONS_SANITARY", "INSPECTIONS_SANITARY_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "environmental-indoor-air",
    label: "Indoor Air Quality",
    category: "Environmental Health",
    routes: ["EH_INDOOR_AIR_QUALITY_LIST", "EH_INDOOR_AIR_QUALITY_CREATE"],
    defaultClassifications: AIRPORT_SEAPORT,
  },
  {
    id: "environmental-noise",
    label: "Noise Monitoring",
    category: "Environmental Health",
    routes: ["EH_NOISE_MONITORING_LIST", "EH_NOISE_MONITORING_CREATE"],
    defaultClassifications: AIRPORT_INLAND,
  },
  {
    id: "environmental-health-education",
    label: "Health Education Activities",
    category: "Environmental Health",
    routes: ["EH_HEALTH_EDUCATION_LIST", "EH_HEALTH_EDUCATION_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "environmental-trainings-meetings",
    label: "Trainings & Meetings",
    category: "Environmental Health",
    routes: ["EH_TRAINING_MEETINGS_LIST", "EH_TRAINING_MEETINGS_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "licensing-overview",
    label: "Licensing Overview",
    category: "Licensing",
    routes: ["LICENSING"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "licences-premises",
    label: "Premise Licences",
    category: "Licensing",
    routes: ["LICENCES_PREMISES", "LICENCES_PREMISES_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "food-handler-certificates",
    label: "Food Handler Certificates",
    category: "Licensing",
    routes: ["FOOD_HANDLER_CERTS", "FOOD_HANDLER_CERTS_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "licence-expiry-watchlist",
    label: "Licence Expiry Watchlist",
    category: "Licensing",
    routes: ["LICENCE_EXPIRY"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "human-remains-overview",
    label: "Human Remains Overview",
    category: "Human Remains",
    routes: ["HUMAN_REMAINS"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "hr-clearance",
    label: "Clearance Requests",
    category: "Human Remains",
    routes: ["HR_CLEARANCE_LIST", "HR_CLEARANCE"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "hr-certificates",
    label: "Clearance Certificates",
    category: "Human Remains",
    routes: ["HR_CERTIFICATES", "HR_CERTIFICATES_VIEW"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "hr-doa-cases",
    label: "Dead on Arrival Cases",
    category: "Traveller",
    routes: ["HR_DOA_CASES_LIST", "HR_DOA_CASES_CREATE"],
    defaultClassifications: AIRPORT_ONLY,
  },
  {
    id: "equipment-overview",
    label: "Equipment Overview",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_WATER_SAMPLING_LIST"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "equipment-water",
    label: "Water Sampling Kits",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_WATER_SAMPLING_LIST", "EQUIPMENT_WATER_SAMPLING_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "equipment-food",
    label: "Food Sampling Kits",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_FOOD_SAMPLING_LIST", "EQUIPMENT_FOOD_SAMPLING_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "equipment-rapid-diagnostics",
    label: "Rapid Diagnostic Kits",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_RAPID_DIAGNOSTIC_LIST", "EQUIPMENT_RAPID_DIAGNOSTIC_CREATE"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "equipment-radiation",
    label: "Radiation Monitors",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_RADIATION_MONITORS_LIST", "EQUIPMENT_RADIATION_MONITORS_CREATE"],
    defaultClassifications: AIRPORT_SEAPORT,
  },
  {
    id: "equipment-monitoring-badges",
    label: "Monitoring Badges",
    category: "Equipment & Assets",
    routes: ["EQUIPMENT_MONITORING_BADGES_LIST", "EQUIPMENT_MONITORING_BADGES_CREATE"],
    defaultClassifications: AIRPORT_ONLY,
  },
  {
    id: "vector-overview",
    label: "Vector Surveillance Overview",
    category: "Vector & Waste",
    routes: ["VECTOR"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "vector-surveillance",
    label: "Vector Monitoring",
    category: "Vector & Waste",
    routes: [
      "VECTOR_MONITORING",
      "VECTOR_MONITORING_CREATE",
      "VECTOR_MONITORING_VIEW",
      "VECTOR_MONITORING_EDIT",
      "VECTOR_CONTROL",
      "VECTOR_ALERTS",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "vector-ovitraps",
    label: "Ovitrap Monitoring",
    category: "Vector & Waste",
    routes: ["VECTOR_OVITRAPS_LIST", "VECTOR_OVITRAPS_CREATE"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "vector-residual-spraying",
    label: "Residual Spraying",
    category: "Vector & Waste",
    routes: [
      "VECTOR_RESIDUAL_SPRAY_LIST",
      "VECTOR_RESIDUAL_SPRAY_CREATE",
      "VECTOR_RESIDUAL_SPRAY_EDIT",
      "VECTOR_RESIDUAL_SPRAY_VIEW",
    ],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "vector-tyres",
    label: "Used Tyres Inspection",
    category: "Vector & Waste",
    routes: [
      "VECTOR_TYRES_LIST",
      "VECTOR_TYRES_CREATE",
      "VECTOR_TYRE_INSPECTIONS_LIST",
      "VECTOR_TYRE_INSPECTIONS_CREATE",
      "VECTOR_TYRES_REPORT_MONTHLY",
    ],
    defaultClassifications: LAND_AND_INLAND,
  },
  {
    id: "vector-rodents",
    label: "Rodent Inspections",
    category: "Vector & Waste",
    routes: ["VECTOR_RODENTS_LIST"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "vector-derating",
    label: "Derating Certificates",
    category: "Vector & Waste",
    routes: ["VECTOR_DERATING_CERTIFICATES_LIST", "VECTOR_DERATING_CERTIFICATES_CREATE"],
    defaultClassifications: AIRPORT_SEAPORT,
  },
  {
    id: "cargo-detention",
    label: "Detention Certificates",
    category: "Cargo",
    routes: ["VECTOR_DETENTION_CERTS_LIST", "VECTOR_DETENTION_CERTS_CREATE", "VECTOR_DETENTION_CERTS_VIEW"],
    defaultClassifications: LAND_SEA_INLAND,
  },
  {
    id: "waste-logs",
    label: "Waste Disposal Logs",
    category: "Vector & Waste",
    routes: ["WASTE", "WASTE_LOGS", "WASTE_LOGS_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "waste-corrective-actions",
    label: "Corrective Actions",
    category: "Vector & Waste",
    routes: ["WASTE_ACTIONS"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "outbreak-overview",
    label: "Outbreak Response Overview",
    category: "Outbreak & Response",
    routes: ["OUTBREAK_ALERTS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "outbreak-alerts",
    label: "Case/Event Register",
    category: "Outbreak & Response",
    routes: ["OUTBREAK_ALERTS", "OUTBREAK_ALERTS_CREATE", "OUTBREAK_ALERTS_VIEW", "OUTBREAK_ALERTS_EDIT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "outbreak-contact-tracing",
    label: "Contact Tracing",
    category: "Outbreak & Response",
    routes: [
      "OUTBREAK_CONTACT_TRACING",
      "OUTBREAK_CONTACT_TRACING_CREATE",
      "OUTBREAK_CONTACT_TRACING_CASE",
    ],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "outbreak-drills",
    label: "Simulation Exercise Planning",
    category: "Outbreak & Response",
    routes: ["OUTBREAK_DRILLS", "OUTBREAK_DRILLS_CREATE", "OUTBREAK_DRILLS_VIEW", "OUTBREAK_DRILLS_EDIT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "outbreak-logbook",
    label: "Incident Logbook",
    category: "Outbreak & Response",
    routes: ["OUTBREAK_LOGBOOK", "OUTBREAK_LOGBOOK_CREATE", "OUTBREAK_LOGBOOK_VIEW", "OUTBREAK_LOGBOOK_EDIT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "outbreak-rumour-register",
    label: "Rumour Register",
    category: "Outbreak & Response",
    routes: [
      "OUTBREAK_RUMOUR_REGISTER",
      "OUTBREAK_RUMOUR_REGISTER_CREATE",
      "OUTBREAK_RUMOUR_REGISTER_VIEW",
      "OUTBREAK_RUMOUR_REGISTER_EDIT",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "outbreak-emergency-plans",
    label: "Emergency Plans",
    category: "Outbreak & Response",
    routes: ["OUTBREAK_EMERGENCY_PLANS", "OUTBREAK_EMERGENCY_PLANS_CREATE", "OUTBREAK_EMERGENCY_PLANS_VIEW", "OUTBREAK_EMERGENCY_PLANS_EDIT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "finance-overview",
    label: "Finance Overview",
    category: "Finance",
    routes: ["FINANCE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "finance-receipts",
    label: "Revenue & Receipts",
    category: "Finance",
    routes: ["FINANCE_RECEIPTS"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "finance-audit",
    label: "Payments & Audit",
    category: "Finance",
    routes: ["FINANCE_AUDIT"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "finance-tariffs",
    label: "Tariffs & Fees",
    category: "Finance",
    routes: ["FINANCE_TARIFFS"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "notifications-overview",
    label: "Notifications Overview",
    category: "Notifications",
    routes: ["NOTIFICATIONS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "notifications-licence-expiry",
    label: "Licence Expiry Alerts",
    category: "Notifications",
    routes: ["NOTIFY_LICENCE_EXPIRY"],
    defaultClassifications: LAND_AND_AIRPORT,
  },
  {
    id: "notifications-corrective-actions",
    label: "Corrective Action Alerts",
    category: "Notifications",
    routes: ["NOTIFY_CORRECTIVE"],
    defaultClassifications: LAND_BORDER_ONLY,
  },
  {
    id: "notifications-thdf",
    label: "THDF Escalations",
    category: "Notifications",
    routes: ["NOTIFY_THDF"],
    defaultClassifications: AIRPORT_ONLY,
  },
  {
    id: "reports-overview",
    label: "Reports Overview",
    category: "Reports & Dashboards",
    routes: ["REPORTS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-center",
    label: "Reports Center",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CENTER_LIST", "REPORTS_CENTER_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-library",
    label: "Reports Library",
    category: "Reports & Dashboards",
    routes: [
      "REPORTS_DASHBOARDS",
      "REPORTS_SCREENING",
      "REPORTS_HUMAN_REMAINS",
      "REPORTS_AIRCRAFT",
      "REPORTS_CARGO",
      "REPORTS_DAILY_AUDIT",
      "REPORTS_CUSTOM",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-exports",
    label: "Standard Exports",
    category: "Reports & Dashboards",
    routes: [
      "REPORTS_EXPORTS_TYRES",
      "REPORTS_EXPORTS_GD",
      "REPORTS_EXPORTS_SURVEILLANCE",
      "REPORTS_EXPORTS_COC",
      "REPORTS_EXPORTS_EXPIRY",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-conveyance-inspection",
    label: "Conveyance Inspection Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CONVEYANCE_INSPECTION"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-general-declarations",
    label: "General Declarations Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_GENERAL_DECLARATIONS"],
    defaultClassifications: AIRPORT_SEAPORT_INLAND,
  },
  {
    id: "reports-cargo-summary",
    label: "Cargo Summary Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CARGO_SUMMARY"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-cargo-clearance",
    label: "Cargo Clearance Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CARGO_CLEARANCE"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-cargo-hazardous-register",
    label: "Hazardous Cargo Register",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CARGO_HAZARDOUS"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-cargo-condemnation",
    label: "Condemnation Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CARGO_CONDEMNATION"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-capture-summary",
    label: "User Capture Summary",
    category: "Reports & Dashboards",
    routes: ["REPORTS_CAPTURE_SUMMARY"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-human-remains-summary",
    label: "Human Remains Clearance Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_HUMAN_REMAINS_SUMMARY"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "reports-traveller-screening",
    label: "Traveller Screening Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_TRAVELLER_SCREENING"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-traveller-disease",
    label: "Disease & Condition Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_TRAVELLER_DISEASE_CONDITIONS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-environmental-premises",
    label: "Premises Inspection Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_ENVIRONMENTAL_PREMISES"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-environmental-scorecard",
    label: "Sanitary Compliance Scorecard",
    category: "Reports & Dashboards",
    routes: ["REPORTS_ENVIRONMENTAL_SANITARY_SCORECARD"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-water-quality",
    label: "Water Quality Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_WATER_QUALITY"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-noise-quality",
    label: "Noise Quality Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_NOISE_QUALITY"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-air-quality",
    label: "Air Quality Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_AIR_QUALITY"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-vector-surveillance",
    label: "Vector Surveillance Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_VECTOR_SURVEILLANCE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-vector-used-tyres",
    label: "Used Tyre Surveillance Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_VECTOR_USED_TYRES"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-vector-control",
    label: "Vector Control Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_VECTOR_CONTROL"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-waste-management",
    label: "Waste Management Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_WASTE_MANAGEMENT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "reports-audit-trail",
    label: "Audit Trail Report",
    category: "Reports & Dashboards",
    routes: ["REPORTS_AUDIT_TRAIL"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "dashboards-domain",
    label: "Domain Dashboards",
    category: "Reports & Dashboards",
    routes: [
      "DASHBOARDS_PORT_OPERATIONS",
      "DASHBOARDS_TRAVELLER_HEALTH",
      "DASHBOARDS_CONVEYANCE_OVERSIGHT",
      "DASHBOARDS_CARGO_HOLDING_BAY",
      "DASHBOARDS_HUMAN_REMAINS",
      "DASHBOARDS_VECTOR_SURVEILLANCE",
      "DASHBOARDS_ENVIRONMENTAL_HEALTH",
      "DASHBOARDS_OUTBREAKS_EPR",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "dashboards-finance",
    label: "Finance Dashboards",
    category: "Reports & Dashboards",
    routes: ["DASHBOARDS_FINANCE_REVENUE", "DASHBOARDS_FINANCE_ZIMRA", "DASHBOARDS_FINANCE_RECON"],
    defaultClassifications: LAND_SEA_AIR,
  },
  {
    id: "dashboards-admin",
    label: "Admin Dashboards",
    category: "Reports & Dashboards",
    routes: ["DASHBOARDS_ADMIN_AUDIT", "DASHBOARDS_ADMIN_MAKER_CHECKER", "DASHBOARDS_ADMIN_COMPLIANCE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "dashboards-operational",
    label: "Operational Dashboards",
    category: "Reports & Dashboards",
    routes: [
      "DASHBOARDS_OPS_PORT_DAILY",
      "DASHBOARDS_OPS_COMPLIANCE",
      "DASHBOARDS_OPS_RISK",
      "REPORTS_PORT_OPS",
      "REPORTS_VECTOR",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "administration-overview",
    label: "Administration Overview",
    category: "Administration",
    routes: ["ADMIN"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-users",
    label: "User Accounts & Roles",
    category: "Administration",
    routes: ["ADMIN_USERS"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-role-matrix",
    label: "RBAC Matrix",
    category: "Administration",
    routes: ["ADMIN_ROLE_MATRIX"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-poe-onboarding",
    label: "PoE Onboarding",
    category: "Administration",
    routes: ["ADMIN_POE_CREATE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-user-profile",
    label: "Officer Profile",
    category: "Administration",
    routes: ["ADMIN_USER_PROFILE"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-hazardous-banned",
    label: "Hazardous Banned Substances",
    category: "Administration",
    routes: [
      "ADMIN_HAZARDOUS_BANNED_SUBSTANCES_LIST",
      "ADMIN_HAZARDOUS_BANNED_SUBSTANCES_CREATE",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-cargo-alert-configuration",
    label: "Cargo Alert Lookback",
    category: "Administration",
    routes: ["ADMIN_CARGO_ALERT_CONFIGURATION"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-document-requirements",
    label: "Document Requirement Definitions",
    category: "Administration",
    routes: [
      "ADMIN_DOC_REQUIREMENTS_LIST",
      "ADMIN_DOC_REQUIREMENTS_CREATE",
      "ADMIN_DOC_REQUIREMENTS_VIEW",
      "ADMIN_DOC_REQUIREMENTS_EDIT",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-country-documents",
    label: "Country Documentation Mapping",
    category: "Administration",
    routes: [
      "ADMIN_COUNTRY_DOC_LIST",
      "ADMIN_COUNTRY_DOC_CREATE",
      "ADMIN_COUNTRY_DOC_VIEW",
      "ADMIN_COUNTRY_DOC_EDIT",
    ],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "admin-master-data",
    label: "Master Data",
    category: "Administration",
    routes: ["ADMIN_MASTER_DATA"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "sop-repository",
    label: "SOPs & Regulations",
    category: "Administration",
    routes: ["SOP_REPOSITORY_LIST"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
  {
    id: "system-audit-logs",
    label: "System Audit Logs",
    category: "Administration",
    routes: ["AUDIT_LOGS_LIST", "ADMIN_AUDIT"],
    defaultClassifications: LAND_SEA_AIR_INLAND,
  },
] as const satisfies readonly ModuleDefinition[]

export type ModuleId = (typeof moduleDefinitions)[number]["id"]

export const moduleRegistry: ModuleDefinition[] = moduleDefinitions.map((def) => ({
  id: def.id,
  label: def.label,
  category: def.category,
  routes: [...def.routes],
  defaultClassifications: [...def.defaultClassifications],
}))

const moduleMap = new Map<ModuleId, ModuleDefinition>(moduleRegistry.map((m) => [m.id as ModuleId, m]))

const routeModuleMap = new Map<keyof typeof PATHS, ModuleDefinition>()
moduleRegistry.forEach((module) => {
  module.routes.forEach((route) => {
    if (!routeModuleMap.has(route)) {
      routeModuleMap.set(route, module)
    }
  })
})

export function getModuleDefinition(id: ModuleId): ModuleDefinition | undefined {
  return moduleMap.get(id)
}

export function getDefaultModulesForClassification(classification: FacilityClassification): ModuleId[] {
  return moduleRegistry
    .filter((module) => module.defaultClassifications.includes(classification))
    .map((module) => module.id as ModuleId)
}

const primaryRouteChecks: ((route: keyof typeof PATHS) => boolean)[] = [
  (route) => String(route).endsWith("_LIST"),
  (route) => String(route).endsWith("_ROOT"),
  (route) => route === "ROOT",
]

export function getPrimaryRouteForModule(module: ModuleDefinition): (keyof typeof PATHS) | undefined {
  for (const predicate of primaryRouteChecks) {
    const match = module.routes.find((route) => Boolean(PATHS[route]) && predicate(route))
    if (match) {
      return match
    }
  }
  return module.routes.find((route) => Boolean(PATHS[route]))
}

export function findModuleForRoute(route: keyof typeof PATHS): ModuleDefinition | undefined {
  return routeModuleMap.get(route)
}

export function getModulesByCategory(): Map<string, ModuleDefinition[]> {
  const groups = new Map<string, ModuleDefinition[]>()
  moduleRegistry.forEach((module) => {
    const existing = groups.get(module.category)
    if (existing) {
      existing.push(module)
    } else {
      groups.set(module.category, [module])
    }
  })
  return groups
}
